<template>
  <div
    class="py-6 px-6 bg-pure-white rounded-xl"
    :style="{ height: '26rem', width: '51.25rem' }"
  >
    <div class="mb-1 flex items-center justify-between">
      <BlTextTitle4 text="Prospects count by stage" class="text-slate-900" />
      <router-link
        :to="
          `/analytics/acquisition-overview?start_date=${dateInput.model[0].start_date}&end_date=${dateInput.model[0].end_date}&group_by=${groupByInput.model.value}`
        "
      >
        <BlInputButton type="outlined" dense label="Details" />
      </router-link>
    </div>
    <div class="mb-5 flex items-center gap-2">
      <BlInputFilter
        :activeLabel="dateActiveLabel"
        title="Date"
        :defaultValue="[defaultDate]"
        :input="dateInput"
        @changed="onChangeDates"
      />
      <BlInputSelect :input="stageInput" @changed="onChangeHandler" />
      <BlInputSelect :input="groupByInput" @changed="onChangeHandler" />
    </div>
    <div v-if="fetchLoading" class="h-72 grid place-content-center">
      <div class="blum-loader"></div>
    </div>
    <div v-else-if="!graphData" class="h-72 grid place-content-center">
      <BlTextBody2 :text="`Data not found.`" />
    </div>
    <div v-else class="flex flex-col">
      <BarChart
        :labels="barChartData.labels"
        :datasets="barChartData.datasets"
        stacked
        width="calc(51.25rem - 3rem)"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import BarChart from "../graphs/BarChart.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputFilter from "../../BlInputFilter.vue";
import BlInputSelect from "../../BlInputSelect.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "CenterOverviewProspectsCountByStage",
  components: {
    BarChart,
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputFilter,
    BlInputSelect,
    BlInputButton
  },
  data() {
    const interval = "year";
    const defaultDate = {
      start_date: moment()
        .startOf(interval)
        .format("YYYY-MM-DD"),
      end_date: moment()
        .endOf(interval)
        .format("YYYY-MM-DD")
    };
    const defaultGroupBy = { name: "Month", value: "month" };

    return {
      defaultDate,
      defaultGroupBy,
      /** */
      fetchLoading: false,
      graphData: null,
      /** */
      dateInput: {
        model: [defaultDate],
        tabs: [{ name: "Date", kind: "date" }],
        dense: true
      },
      stageInput: {
        model: {
          value: "",
          name: "Stage",
          is_discontinued: false,
          is_admitted: false
        },
        prefix: "Stage",
        combine: true,
        dense: true,
        options: []
      },
      groupByInput: {
        model: defaultGroupBy,
        prefix: "Group by",
        combine: true,
        dense: true,
        options: [
          { name: "Day", value: "day", disabled: false },
          { name: "Week", value: "week", disabled: false },
          { name: "Month", value: "month" },
          { name: "Year", value: "year" }
        ]
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "stages"]),
    searchParams() {
      return this.$route.query || {};
    },
    dateActiveLabel() {
      const start = this.dateInput.model[0].start_date;
      const end = this.dateInput.model[0].end_date;

      const sYYYY = moment(start).format("YYYY");
      const sMMM = moment(start).format("MMM");
      const sDD = moment(start).format("DD");

      const eYYYY = moment(end).format("YYYY");
      const eMMM = moment(end).format("MMM");
      const eDD = moment(end).format("DD");

      let date = "";
      if (sYYYY === eYYYY) {
        if (sMMM === eMMM) date = `${sDD} - ${eDD} ${sMMM}, ${sYYYY}`;
        else if (sMMM === "Jan" && eMMM === "Dec") date = `${sYYYY}`;
        else date = `${sDD} ${sMMM} - ${eDD} ${eMMM}, ${sYYYY}`;
      } else date = `${sYYYY} - ${eYYYY}`;
      return `
        <span class="flex items-center gap-2">
          <span>Date</span>
          <b>${date}</b>
        </span>
      `;
    },
    /*
    {
      "date": "2024-01-01",
      "total": 0
    },
    */
    barChartData() {
      const n = { labels: [], datasets: [], empty: true };
      const raw = this.graphData;
      if (!raw) return n;
      const keys = ["total"];
      let colors = ["#0095D1"];
      if (this.stageInput.model.is_discontinued) {
        colors = ["#F63D68"]; // rose-7
      }
      if (this.stageInput.model.is_pending) {
        colors = ["#E8B100"]; // tulip-7
      }
      if (this.stageInput.model.is_admitted) {
        colors = ["#B9D843"]; // lime-5
      }
      const texts = this.stageInput.model.name;

      const labels = [];
      const datasets = keys.map((key, idx) => ({
        label: texts,
        data: [],
        color: colors[idx]
      }));
      let empty = true;

      const formats = {
        day: "DD/MM/YY",
        week: "DD/MM",
        month: "MMM YYYY",
        year: "YYYY"
      };
      const groupBy = this.groupByInput.model.value;

      raw.forEach(i => {
        let l = moment(i.date)
          .format(formats[groupBy])
          .toUpperCase();
        if (groupBy === "week") {
          l += `-${moment(i.date)
            .add(6, "days")
            .format(formats[groupBy])}`;
        }
        labels.push(l);
        keys.forEach((key, idx) => {
          const value = i[key] || 0;
          datasets[idx].data.push(value);
          if (value) empty = false;
        });
      });

      return { labels, datasets, empty };
    }
  },
  watch: {
    stages: {
      deep: true,
      immediate: true,
      handler(val) {
        const arr = val.map(i => ({
          name: i.name,
          value: i.id,
          is_admitted: i.is_admitted,
          is_pending: i.is_pending,
          is_discontinued: i.is_discontinued
        }));
        this.stageInput.options = arr;
        this.stageInput.model = arr[0];
        this.fetchGraph();
      }
    }
  },
  mounted() {
    // this.fetchGraph();
  },
  methods: {
    ...mapActions(["actionGet"]),
    fetchGraph() {
      const start = this.dateInput.model[0].start_date;
      const end = this.dateInput.model[0].end_date;

      this.groupByInput.options[0].disabled = false;
      this.groupByInput.options[1].disabled = false;
      if (start && end) {
        const diff = moment(end).diff(moment(start), "days") + 1;
        if (diff >= 365) {
          this.groupByInput.options[0].disabled = true;
          this.groupByInput.options[1].disabled = true;
          const groupByDayOrWeek =
            this.groupByInput.model.value === "day" ||
            this.groupByInput.model.value === "week";
          if (groupByDayOrWeek) {
            this.groupByInput.model = { name: "Month", value: "month" };
          }
        }
      }

      let params = `?start_date=${start}&end_date=${end}&calculation_method=prospects_per_stage`;
      params += `&stage_id=${this.stageInput.model.value}`;
      params += `&group_by=${this.groupByInput.model.value}`;
      if (this.searchParams["branch"]) {
        params += `&branch_ids=${this.searchParams["branch"]}`;
      }
      const data = {
        url: `/api/v1/analytics/acquisition_comparison${params}`
      };
      this.fetchLoading = true;
      this.graphData = null;
      this.actionGet(data).then(({ success, data }) => {
        this.fetchLoading = false;
        if (!success) return;
        this.graphData = data;
      });
    },
    onChangeDates(payload) {
      payload.input.model = payload.value;
      this.fetchGraph();
    },
    onChangeHandler(payload) {
      payload.input.model = payload.value;
      this.fetchGraph();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/analytics/mainDashboard/ProspectsCountByStage.vue